import { useMemo } from 'react';
import { Outlet } from 'react-router-dom';
import { hasAlliancePermission } from '@kaeliux/game-engine';
import { useMyAlliance } from '../../hooks/useMyAlliance';
import { useTeamPlanet } from '../../hooks/useTeamPlanet';
import { useTeamPlanetView } from '../../hooks/useTeamPlanetView';
import { useShellChrome } from '../../hooks/useShellChrome';
import { TeamPlanetViewContext } from '../../contexts/TeamPlanetViewContext';
import { TeamPlanetContext, type TeamPlanetSession } from '../../contexts/TeamPlanetContext';
import { Panel } from '../ui/Panel';
import { Skeleton } from '../ui/Skeleton';
import TeamPlanetEmptyState from './TeamPlanetEmptyState';

/** Polled because the Team planet sits outside the app-wide game loop. */
const TEAM_PLANET_POLL_MS = 15_000;

function errorMessage(error: unknown, fallback: string): string {
    return error instanceof Error && error.message ? error.message : fallback;
}

/**
 * Route wrapper for every `/team-planet/*` screen. Loads the viewer's
 * alliance and its Team planet, then runs the shared planet screens under
 * the Team planet's `PlanetView` (its own mutations, queue limits and
 * permissions) instead of the personal planet stores. The shell chrome
 * outlet context is forwarded unchanged so `useShellChrome` keeps working
 * in the nested pages.
 */
export default function TeamPlanetLayout() {
    const shell = useShellChrome();
    const allianceQuery = useMyAlliance();
    const detail = allianceQuery.data ?? null;
    const teamPlanetQuery = useTeamPlanet({ enabled: !!detail, refetchInterval: TEAM_PLANET_POLL_MS });

    const viewerRole = detail?.viewerRole ?? null;
    const canManage = hasAlliancePermission(viewerRole, 'manageTeamPlanet');
    const teamPlanet = teamPlanetQuery.data ?? null;
    const view = useTeamPlanetView(teamPlanet, canManage);

    const session = useMemo<TeamPlanetSession | null>(
        () => (teamPlanet && detail
            ? { teamPlanet, alliance: detail.alliance, viewerRole, canManage, isLeader: viewerRole === 'leader' }
            : null),
        [teamPlanet, detail, viewerRole, canManage],
    );

    if (allianceQuery.isPending || (detail && teamPlanetQuery.isPending)) {
        return (
            <main className="max-w-7xl mx-auto w-full p-4 flex flex-col gap-4" aria-busy="true">
                <Skeleton variant="block" className="h-[420px]" />
                <div className="grid gap-4 lg:grid-cols-3">
                    <Skeleton variant="block" className="h-40" />
                    <Skeleton variant="block" className="h-40" />
                    <Skeleton variant="block" className="h-40" />
                </div>
            </main>
        );
    }

    if (allianceQuery.isError || teamPlanetQuery.isError) {
        return (
            <main className="max-w-7xl mx-auto w-full p-4">
                <Panel variant="solid" padding="md">
                    <p className="text-status-danger" role="alert">
                        {errorMessage(allianceQuery.error ?? teamPlanetQuery.error, 'Failed to load the Team planet.')}
                    </p>
                </Panel>
            </main>
        );
    }

    if (!detail || !session) {
        return (
            <main className="max-w-7xl mx-auto w-full p-4">
                <TeamPlanetEmptyState
                    reason={detail ? 'no-team-planet' : 'no-alliance'}
                    isLeader={viewerRole === 'leader'}
                />
            </main>
        );
    }

    return (
        <TeamPlanetContext.Provider value={session}>
            <TeamPlanetViewContext.Provider value={view}>
                <Outlet context={shell} />
            </TeamPlanetViewContext.Provider>
        </TeamPlanetContext.Provider>
    );
}
